import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import { CreateFieldTexts } from "../../utils/customerCreateFieldUtils";
import { TextFieldStyles } from "../../styles/commonStyles";
import { renderMenuItems } from "../forms/utils";
import { Add, Close, Edit } from "@mui/icons-material";
import FormActionButton from "../buttons/formActionButton.component";
import Map from "../map/map.component";
import { useEffect, useState } from "react";
import { validateSingleSubArea } from "../stepper/validationUtils";
import { isSubPolygonValid } from "../map/mapUtils";
import { useIsMobile } from "../../hook/useIsMobile";
import { Controller, useForm } from "react-hook-form";

const SubAreaDialog = ({
  open,
  onClose,
  fieldsType,
  subAreaData,
  onSubAreaAdd,
  onSubAreaUpdate,
  subAreas,
  fieldPosition,
}) => {
  const isMobile = useIsMobile();
  const [subPolygonCoordinates, setSubPolygonCoordinates] = useState([]);
  const [validationError, setValidationError] = useState(null);
  const [mapError, setMapError] = useState(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      subAreaName: "",
      subAreaType: "",
      organicSubstance: "",
      stoneDensity: "",
    },
  });

  const handleSubPolygonCreated = (newPolygon) => {
    const formattedPolygon = newPolygon.map(({ lat, lng }) => [lat, lng]);

    const existingSubPolygons = subAreas
      ? subAreas
          .filter((subArea) => subArea?.id !== subAreaData?.id)
          .map((subArea) => subArea.subPolygonCoordinates)
          .filter((coords) => coords && coords.length > 0)
      : [];

    const validationResult = isSubPolygonValid(
      formattedPolygon,
      fieldPosition,
      existingSubPolygons
    );

    if (!validationResult.isValid) {
      setMapError(validationResult.error);
      setValidationError({
        ...validationError,
        subPolygonCoordinates: validationResult.error,
      });
      return;
    }

    setMapError(null);
    if (validationError?.subPolygonCoordinates) {
      const newErrors = { ...validationError };
      delete newErrors.subPolygonCoordinates;
      setValidationError(Object.keys(newErrors).length > 0 ? newErrors : null);
    }
    setSubPolygonCoordinates(formattedPolygon);
  };

  const onSubmit = (data) => {
    if (!subPolygonCoordinates || subPolygonCoordinates.length === 0) {
      setMapError("E' necessario disegnare un poligono sulla mappa");
      return;
    }
    const subAreaToSave = {
      ...data,
      subPolygonCoordinates,
      id: subAreaData?.id || Date.now(),
    };

    const errors = validateSingleSubArea(subAreaToSave, fieldPosition);

    if (errors) {
      setValidationError(errors);
      return;
    }

    setValidationError(null);

    if (subAreaData) {
      onSubAreaUpdate(subAreaToSave);
    } else {
      onSubAreaAdd(subAreaToSave);
    }

    handleClose();
  };

  const handleClose = () => {
    setSubPolygonCoordinates([]);
    setValidationError(null);
    onClose();
  };

  useEffect(() => {
    if (subAreaData?.polygon) {
      setSubPolygonCoordinates(subAreaData.polygon);
    } else {
      setSubPolygonCoordinates([]);
    }
  }, [subAreaData]);

  useEffect(() => {
    if (subAreaData) {
      reset({
        subAreaName: subAreaData?.name || subAreaData?.subAreaName || "",
        subAreaType:
          subAreaData?.field_type?.id || subAreaData?.subAreaType || "",
        organicSubstance:
          subAreaData?.organic_soil_percentage ||
          subAreaData?.organicSubstance ||
          "",
        stoneDensity:
          subAreaData?.stone_percentage || subAreaData?.stoneDensity || "",
      });
      setSubPolygonCoordinates(
        subAreaData.polygon || subAreaData.subPolygonCoordinates || []
      );
    } else {
      reset({
        subAreaName: "",
        subAreaType: "",
        organicSubstance: "",
        stoneDensity: "",
      });
      setSubPolygonCoordinates([]);
    }
    setMapError(null);
  }, [reset, subAreaData]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="subArea-dialog"
      aria-describedby="create new sub area"
      maxWidth="lg"
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={() => ({
          position: "absolute",
          right: 8,
          top: 8,
        })}
      >
        <Close sx={{ width: "20px", height: "20px", color: "text.primary" }} />
      </IconButton>
      <DialogTitle
        id="subArea-dialog-title"
        sx={{ fontWeight: "bold", fontSize: "16px" }}
      >
        {subAreaData ? "Modifica sottoterreno" : "Nuovo sottoterreno"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          id="subArea-dialog-description"
          sx={{ fontSize: "12px" }}
        >
          {subAreaData
            ? CreateFieldTexts.subAreaDialogEditDescription
            : CreateFieldTexts.subAreaDialogAddDescription}
        </DialogContentText>
        <Box
          component="form"
          id="subArea-form"
          onSubmit={handleSubmit(onSubmit)}
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          gap={2}
        >
          <Box display="flex" flex={1} flexDirection="column" gap={2}>
            <TextField
              variant="filled"
              id="subAreaName"
              label="Nome sottoterreno*"
              fullWidth
              sx={TextFieldStyles}
              aria-label="Nome sottoterreno"
              {...register("subAreaName", {
                required: "Nome sottoterreno obbligatorio",
              })}
              error={!!errors?.subAreaName}
              helperText={errors?.subAreaName?.message}
            />
            <Controller
              name="subAreaType"
              control={control}
              rules={{ required: "Tipologia terreno obbligatoria" }}
              render={({ field }) => (
                <TextField
                  variant="filled"
                  select
                  id="subAreaType"
                  label="Tipologia terreno*"
                  fullWidth
                  sx={TextFieldStyles}
                  aria-label="Tipologia terreno"
                  {...field}
                  error={!!errors?.subAreaType}
                  helperText={errors?.subAreaType?.message}
                >
                  {renderMenuItems(fieldsType)}
                </TextField>
              )}
            />

            <Stack spacing={2} direction="row">
              <TextField
                variant="filled"
                id="organicSubstance"
                label="Sostanza organica %"
                fullWidth
                sx={TextFieldStyles}
                aria-label="Sostanza organica %"
                type="number"
                {...register("organicSubstance", {
                  max: {
                    value: 100,
                    message: "Il valore deve essere minore o uguale a 100",
                  },
                })}
                error={!!errors?.organicSubstance}
                helperText={errors?.organicSubstance?.message}
              />
              <TextField
                variant="filled"
                id="stoneDensity"
                label="Densità pietre %"
                fullWidth
                sx={TextFieldStyles}
                aria-label="Densità pietre"
                type="number"
                {...register("stoneDensity", {
                  max: {
                    value: 100,
                    message: "Il valore deve essere minore o uguale a 100",
                  },
                })}
                error={!!errors?.stoneDensity}
                helperText={errors?.stoneDensity?.message}
              />
            </Stack>
            {mapError && (
              <Box sx={{ color: "error.main", fontSize: "0.75rem", mt: 1 }}>
                {mapError}
              </Box>
            )}
          </Box>
          <Box width={isMobile ? "100%" : "500px"} height="400px">
            <Map
              isDrawable={true}
              onPolygonCreated={handleSubPolygonCreated}
              polygonCoordinates={fieldPosition}
              zoomLevel={7}
              fieldPosition={fieldPosition[0]}
              subPolygonCoordinates={subPolygonCoordinates}
              subAreas={subAreas}
              mode="sub"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <FormActionButton
          startIcon={subAreaData ? <Edit /> : <Add />}
          label={subAreaData ? "Modifica" : "Aggiungi"}
          onClick={handleSubmit(onSubmit)}
        />
      </DialogActions>
    </Dialog>
  );
};

export default SubAreaDialog;
